import { gql } from '@apollo/client/core';

/**
 * GraphQL-операции мессенджера (queries + mutations).
 *
 * Осознанное отступление от правила «mutations в features» (см. план D9):
 * entity-композаблы оркеструют мутации + кэш и потребляются страницей/виджетами.
 */

export const MESSENGER_CHANNEL_FRAGMENT = gql`
  fragment MessengerChannelFragment on MessengerChannel {
    id
    title
    provider
    is_active
    is_fallback
    priority
    conversation_count
    health_status {
      status
      webhook {
        status
        error
        checked_at
      }
      api {
        status
        error
        checked_at
      }
    }
    credentials {
      token
      username
      access_token
      group_id
      confirmation
      secret
      screen_name
    }
    created_at
    updated_at
  }
`;

export const GET_MESSENGER_CHANNELS = gql`
  query GetMessengerChannels {
    messengerChannels {
      ...MessengerChannelFragment
    }
  }
  ${MESSENGER_CHANNEL_FRAGMENT}
`;

export const CREATE_MESSENGER_CHANNEL = gql`
  mutation CreateMessengerChannel($input: CreateMessengerChannelInput!) {
    createMessengerChannel(input: $input) {
      ...MessengerChannelFragment
    }
  }
  ${MESSENGER_CHANNEL_FRAGMENT}
`;

export const UPDATE_MESSENGER_CHANNEL = gql`
  mutation UpdateMessengerChannel($id: ID!, $input: UpdateMessengerChannelInput!) {
    updateMessengerChannel(id: $id, input: $input) {
      ...MessengerChannelFragment
    }
  }
  ${MESSENGER_CHANNEL_FRAGMENT}
`;

export const DELETE_MESSENGER_CHANNEL = gql`
  mutation DeleteMessengerChannel($id: ID!) {
    deleteMessengerChannel(id: $id)
  }
`;

// --- Диалоги: фрагменты и мутации привязки ----------------------------------

export const MESSENGER_CLIENT_FRAGMENT = gql`
  fragment MessengerClientFragment on MessengerClient {
    id
    first_name
    last_name
    phone_masked
    type
    marketing_source {
      id
      title
    }
    city {
      id
      name
    }
    active_bookings_count
  }
`;

export const MESSENGER_CONVERSATION_FRAGMENT = gql`
  fragment MessengerConversationFragment on MessengerConversation {
    id
    channel_id
    channel {
      id
      title
      provider
      is_active
      priority
    }
    client {
      ...MessengerClientFragment
    }
    chat_metadata {
      first_name
      last_name
      username
      avatar_url
      type
    }
    external_chat_id
    is_active
    is_default
    connected_at
    disconnected_at
    last_message_at
    messages_count
    created_at
    updated_at
  }
  ${MESSENGER_CLIENT_FRAGMENT}
`;

export const LINK_CONVERSATION_CLIENT = gql`
  mutation LinkConversationClient($conversationId: ID!, $clientId: ID!, $force: Boolean) {
    linkConversationClient(conversationId: $conversationId, clientId: $clientId, force: $force) {
      conversation {
        ...MessengerConversationFragment
      }
      warnings
    }
  }
  ${MESSENGER_CONVERSATION_FRAGMENT}
`;

export const UNLINK_CONVERSATION_CLIENT = gql`
  mutation UnlinkConversationClient($conversationId: ID!) {
    unlinkConversationClient(conversationId: $conversationId) {
      ...MessengerConversationFragment
    }
  }
  ${MESSENGER_CONVERSATION_FRAGMENT}
`;

export const SET_CONVERSATION_DEFAULT = gql`
  mutation SetConversationDefault($conversationId: ID!) {
    setConversationDefault(conversationId: $conversationId) {
      ...MessengerConversationFragment
    }
  }
  ${MESSENGER_CONVERSATION_FRAGMENT}
`;

export const CREATE_CLIENT_FROM_CONVERSATION = gql`
  mutation CreateClientFromConversation($conversationId: ID!, $input: CreateClientFromConversationInput!) {
    createClientFromConversation(conversationId: $conversationId, input: $input) {
      conversation {
        ...MessengerConversationFragment
      }
      existing
      warnings
    }
  }
  ${MESSENGER_CONVERSATION_FRAGMENT}
`;

// --- Сообщения: фрагмент и списки диалогов ----------------------------------

export const MESSENGER_MESSAGE_FRAGMENT = gql`
  fragment MessengerMessageFragment on MessengerMessage {
    id
    conversation_id
    channel_id
    direction
    sender_type
    message_type
    messenger_type
    employee_name
    body
    body_preview
    status
    external_message_id
    metadata
    file_url
    file_name
    file_size
    failed_reason
    scheduled_at
    sent_at
    delivered_at
    deliver_deadline
    read_at
    deleted_at
    created_at
    updated_at
  }
`;

export const MESSENGER_DIALOG_FRAGMENT = gql`
  fragment MessengerDialogFragment on MessengerDialog {
    id
    status
    waiting_since
    assignee {
      id
      first_name
      last_name
    }
  }
`;

export const GET_MESSENGER_CONVERSATION_GROUPS = gql`
  query GetMessengerConversationGroups($search: String, $page: Int!, $perPage: Int!, $section: MessengerDialogSection) {
    messengerConversationGroups(search: $search, page: $page, perPage: $perPage, section: $section) {
      groups {
        client_id
        client {
          ...MessengerClientFragment
        }
        conversations {
          ...MessengerConversationFragment
        }
        last_message {
          ...MessengerMessageFragment
        }
        unread_count
        last_message_at
        dialog {
          ...MessengerDialogFragment
        }
      }
      meta {
        current_page
        per_page
        total
        last_page
        has_more
      }
    }
  }
  ${MESSENGER_CONVERSATION_FRAGMENT}
  ${MESSENGER_MESSAGE_FRAGMENT}
  ${MESSENGER_DIALOG_FRAGMENT}
`;

export const GET_MESSENGER_CONVERSATION_GROUP = gql`
  query GetMessengerConversationGroup($conversationId: ID!) {
    messengerConversationGroup(conversationId: $conversationId) {
      client_id
      client {
        ...MessengerClientFragment
      }
      conversations {
        ...MessengerConversationFragment
      }
      last_message {
        ...MessengerMessageFragment
      }
      unread_count
      last_message_at
      dialog {
        ...MessengerDialogFragment
      }
    }
  }
  ${MESSENGER_CONVERSATION_FRAGMENT}
  ${MESSENGER_MESSAGE_FRAGMENT}
  ${MESSENGER_DIALOG_FRAGMENT}
`;

export const GET_MESSENGER_DIALOG_COUNTS = gql`
  query GetMessengerDialogCounts {
    messengerDialogCounts {
      open
      unanswered
      resolved
    }
  }
`;

export const GET_MESSENGER_OPERATORS = gql`
  query GetMessengerOperators {
    messengerOperators {
      id
      first_name
      last_name
    }
  }
`;

export const GET_CLIENT_MESSENGER_CONVERSATIONS = gql`
  query GetClientMessengerConversations($clientId: ID!) {
    clientMessengerConversations(clientId: $clientId) {
      ...MessengerConversationFragment
    }
  }
  ${MESSENGER_CONVERSATION_FRAGMENT}
`;

export const GET_MESSENGER_CLIENT_NEXT_BOOKING = gql`
  query GetMessengerClientNextBooking($clientId: ID!) {
    messengerClientNextBooking(clientId: $clientId) {
      start
      end
      status
      service_variant_title
      location_title
    }
  }
`;

export const GET_CONVERSATION_MESSAGES = gql`
  query GetConversationMessages($conversationId: ID!, $beforeMessageId: ID, $perPage: Int!, $filter: MessageFilter!) {
    conversationMessages(
      conversationId: $conversationId
      beforeMessageId: $beforeMessageId
      perPage: $perPage
      filter: $filter
    ) {
      messages {
        ...MessengerMessageFragment
      }
      has_more
      scheduled_count
    }
  }
  ${MESSENGER_MESSAGE_FRAGMENT}
`;

export const GET_MESSENGER_MESSAGE = gql`
  query GetMessengerMessage($id: ID!) {
    messengerMessage(id: $id) {
      ...MessengerMessageFragment
    }
  }
  ${MESSENGER_MESSAGE_FRAGMENT}
`;

export const SEND_MESSENGER_MESSAGE = gql`
  mutation SendMessengerMessage($input: SendMessengerMessageInput!) {
    sendMessengerMessage(input: $input) {
      ...MessengerMessageFragment
    }
  }
  ${MESSENGER_MESSAGE_FRAGMENT}
`;

export const DELETE_MESSENGER_MESSAGE = gql`
  mutation DeleteMessengerMessage($id: ID!) {
    deleteMessengerMessage(id: $id)
  }
`;

export const MARK_CONVERSATION_AS_READ = gql`
  mutation MarkConversationAsRead($conversationId: ID!) {
    markConversationAsRead(conversationId: $conversationId)
  }
`;

export const TAKE_MESSENGER_DIALOG = gql`
  mutation TakeMessengerDialog($dialogId: ID!) {
    takeMessengerDialog(dialogId: $dialogId) {
      ...MessengerDialogFragment
    }
  }
  ${MESSENGER_DIALOG_FRAGMENT}
`;

export const RESOLVE_MESSENGER_DIALOG = gql`
  mutation ResolveMessengerDialog($dialogId: ID!) {
    resolveMessengerDialog(dialogId: $dialogId) {
      ...MessengerDialogFragment
    }
  }
  ${MESSENGER_DIALOG_FRAGMENT}
`;

export const REASSIGN_MESSENGER_DIALOG = gql`
  mutation ReassignMessengerDialog($dialogId: ID!, $employeeId: ID!) {
    reassignMessengerDialog(dialogId: $dialogId, employeeId: $employeeId) {
      ...MessengerDialogFragment
    }
  }
  ${MESSENGER_DIALOG_FRAGMENT}
`;

export const UNASSIGN_MESSENGER_DIALOG = gql`
  mutation UnassignMessengerDialog($dialogId: ID!) {
    unassignMessengerDialog(dialogId: $dialogId) {
      ...MessengerDialogFragment
    }
  }
  ${MESSENGER_DIALOG_FRAGMENT}
`;

export const MARK_MESSENGER_DIALOG_ANSWERED = gql`
  mutation MarkMessengerDialogAnswered($dialogId: ID!) {
    markMessengerDialogAnswered(dialogId: $dialogId) {
      ...MessengerDialogFragment
    }
  }
  ${MESSENGER_DIALOG_FRAGMENT}
`;
